import Environment from '@omni/kit/Environment';
import HealthMonitor from '@omni/kit/HealthMonitor';
import { AuthModal, CredentialProps } from '@omni/kit/auth';
import { validateToken } from '@omni/kit/auth/Service/validateToken';
import { KitButton } from '@omni/kit/components';
import KitAvatar from '@omni/kit/components/KitAvatar';
import KitInfo from '@omni/kit/components/KitInfo';
import KitList from '@omni/kit/components/KitList';
import KitListItem from '@omni/kit/components/KitListItem';
import KitLoader from '@omni/kit/components/KitLoader';
import KitMapView from '@omni/kit/components/KitMapView';
import KitModalV2 from '@omni/kit/components/KitModalV2';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import KitSnackRender from '@omni/kit/components/KitSnackRender';
import KitText from '@omni/kit/components/KitText';
import Show from '@omni/kit/components/Show';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '@omni/kit/constants/identifiers';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import AccountsService from '@omni/kit/services/AccountsService';
import ChatService, { IChannel } from '@omni/kit/services/ChatService';
import GroupsService from '@omni/kit/services/GroupsService';
import {
  GroupRoles,
  IGroup,
  IGroupLocation,
  IGroupMember,
  IJoinRequest,
  IProfile,
} from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Depth from '@omni/kit/theming/Depth';
import Spacing from '@omni/kit/theming/Spacing';
import {
  dismissReactNativeModal,
  dispatchAction,
} from '@omni/kit/utilities/NativeHelpers';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import {
  constructGroupSchedule,
  getGroupTypeDisplayName,
} from '@omni/kit/utilities/groupUtilities';
import {
  createImageUrl,
  isWithinIframe,
  postContentHeight,
  postContentScrollToTop,
} from '@omni/kit/utilities/utilities';
import MessagingChatService from '@omni/messaging/services/ChatService';
import { getInvitedList } from '@omni/messaging/shared/redux/actions/ChatActions';
import { invitedListSelector } from '@omni/messaging/shared/redux/selectors';
import { RequestToJoinModal } from '@omni/messaging/shared/scenes/joinRequests/components/RequestToJoinModal';
import { getShortChannelUrl } from '@omni/messaging/utilities/chatUtilities';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { InteractionManager, Platform, ScrollView, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

const debug = require('debug')('groups:detail');

export type GroupDetailRoute = RouteProp<GroupDetailParamList, 'Result'>;

type GroupDetailParamList = {
  Result: {
    id: string;
    invite_id?: string;
  };
};

interface Props {
  appKey?: string;
  credentialProps?: CredentialProps;
  staticParams?: {
    id: string | undefined;
    invite_id: string | undefined;
    path: string | undefined;
  };
}

const parseStringToValidValue = (v: string | undefined) => {
  return v === 'undefined' || v === undefined ? undefined : v;
};

export default (props: Props): ReactElement => {
  const dispatch = useDispatch();

  const { t } = useTranslation(['groups']);
  const navigation = useNavigation();
  const { params: routeParams } = useRoute<GroupDetailRoute>();
  const { sizeClass } = useSizeClass();

  const params =
    props.staticParams !== undefined ? props.staticParams : routeParams;
  const [snackOptions, setSnackOptions] = useState({
    visible: false,
    message: '',
    duration: KitSnackDuration.SHORT,
  });
  const showKitSnack = (msg: string, dur: number) => {
    setSnackOptions({ visible: true, message: msg, duration: dur });
  };
  const { app, tokens, user } = useShellContext();
  const appKey = app.appKey;
  const accessToken = useMemo(() => tokens.user || '', [tokens.user]);
  const guestToken = useMemo(() => tokens.guest || '', [tokens.guest]);
  const isAuthenticated = useMemo(
    () => validateToken(accessToken, undefined, appKey, undefined),
    [accessToken, appKey]
  );

  const [chatUserResolved, setChatUserResolved] = useState<boolean>(false);

  const invitedList = useSelector(invitedListSelector);

  // Local State
  const [group, setGroup] = useState<IGroup | undefined>();
  const [channel, setChannel] = useState<IChannel | undefined>();
  const [members, setMembers] = useState<IGroupMember[]>([]);
  const [groupLocation, setGroupLocation] = useState<
    IGroupLocation | undefined
  >();

  const [orgKey, setOrgKey] = useState<string | undefined>();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isFetchingGroup, setIsFetchingGroup] = useState(false);
  const [requestToJoinModalVisible, setRequestToJoinModalVisible] =
    useState(false);

  const [requestToJoinStatus, setRequestToJoinStatus] = useState<
    IJoinRequest | undefined
  >();

  const [acceptInviteAfterLogin, setAcceptInviteAfterLogin] = useState(false);
  const [createJoinRequestAfterLogin, setCreateJoinRequestAfterLogin] =
    useState(false);
  const [joinGroupAfterLogin, setJoinGroupAfterLogin] = useState(false);

  const [isJoiningGroup, setIsJoiningGroup] = useState(false);

  // Computed State
  const endUserId = user?.id;
  const managers = useMemo(() => {
    return members.filter((member) => member.role === GroupRoles.Manager);
  }, [members]);
  const enrollmentType = group?.enrollment_type;

  const myselfAsMember = useMemo(() => {
    return members.filter(
      (member) => member._embedded?.['end-user']?.id === endUserId
    )?.[0];
  }, [members, endUserId]);

  const groupSchedule = group && constructGroupSchedule(group);

  const displayAddress = useMemo(() => {
    const title = groupLocation?.title;
    const street = groupLocation?._embedded?.address?.street;
    const city = groupLocation?._embedded?.address?.city;
    const state = groupLocation?._embedded?.address?.state;
    const parsedTitle = parseStringToValidValue(title);
    const parsedStreet = parseStringToValidValue(street);
    const parsedCity = parseStringToValidValue(city);
    const parsedState = parseStringToValidValue(state);

    if (myselfAsMember) {
      if (parsedTitle) {
        if (parsedStreet) {
          return `${parsedTitle} • ${parsedStreet}`;
        }

        return `${parsedTitle} • ${parsedCity} , ${parsedState}`;
      }

      if (!parsedTitle && !parsedStreet) {
        return `${parsedCity}, ${parsedState}`;
      }

      return `${parsedStreet}`;
    }

    if (parsedTitle) {
      return `${parsedTitle} • ${parsedCity}, ${parsedState}`;
    }

    return `${parsedCity}, ${parsedState}`;
  }, [myselfAsMember, groupLocation]);

  /**
   * The invite list will have a single item for the current user
   * when the user has a non-expired pending invite
   * (applies to both regular members and group managers)
   *
   * However, if a group manager has already accepted the invite to be a group manager,
   * then the invite list will contain pending invites for all users.
   * So we take care not to auto accept a pending invite in this case
   * by also checking 'messaging_status' === 'invited'.
   */
  const inviteExpired = useMemo(() => {
    return myselfAsMember?.messaging_status === 'invited' && !invitedList?.[0];
  }, [myselfAsMember, invitedList]);

  // If a user accepts an invite from SMS or on a different email than what was invited,
  // then the invitedList will not have the pending invite,
  // so we rely on the invite_id param on the group detail link to handle this case.
  const [pendingInviteId, setPendingInviteId] = useState<string | undefined>(
    myselfAsMember?.messaging_status === 'invited' && invitedList?.[0]
      ? invitedList[0].id
      : params.invite_id // passed in through a module command from a deep link or through a query param in web messaging
  );

  // the invite list may have changed after the component mounted
  useEffect(() => {
    if (myselfAsMember?.messaging_status === 'invited' && invitedList?.[0]) {
      setPendingInviteId(invitedList[0].id);
    }
  }, [invitedList, myselfAsMember]);

  const onCreateRequest = async () => {
    if (isAuthenticated && group?.id && endUserId) {
      try {
        const response = await GroupsService.CreateJoinRequest({
          groupId: group.id,
          endUserId,
          token: accessToken,
        });

        if (response.status === 201) {
          showKitSnack(t('groups:requestToJoinSent'), KitSnackDuration.LONG);
          setRequestToJoinStatus(response.body);
          setRequestToJoinModalVisible(false);
        }
      } catch (e) {
        console.error(e);
        showKitSnack('An error occurred', KitSnackDuration.SHORT);
      }
    } else {
      if (!isAuthenticated) {
        setRequestToJoinModalVisible(false);
        setShowAuthModal(true);
        setCreateJoinRequestAfterLogin(true);
      }
    }
  };

  // Handlers
  const onPressAcceptInvite = useCallback(async () => {
    if (accessToken && isAuthenticated && pendingInviteId) {
      try {
        const response = await MessagingChatService.patch(
          'channel-invite',
          pendingInviteId,
          {
            accepted: true,
          },
          accessToken
        );

        if (
          response.status === 400 &&
          response.message &&
          response.message.includes('channel_invite has expired')
        ) {
          if (group?.enrollment_type === 'closed') {
            showKitSnack(
              t('groups:expiredInvitationClosedGroup'),
              KitSnackDuration.LONG
            );
          } else {
            showKitSnack(t('groups:expiredInvitation'), KitSnackDuration.LONG);
          }
        }

        setPendingInviteId(undefined);
      } catch (error) {
        HealthMonitor.logError(error);
      } finally {
        // refresh group list for messaging_status change
        fetchGroup();
      }
    } else {
      if (!isAuthenticated) {
        setShowAuthModal(true);
        setAcceptInviteAfterLogin(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, isAuthenticated, pendingInviteId, group?.enrollment_type]);

  const onPressJoinGroup = useCallback(async () => {
    if (isAuthenticated && group?.id && endUserId) {
      setIsJoiningGroup(true);
      try {
        const response = await GroupsService.CreateGroupMember({
          groupId: group.id,
          endUserId,
          token: accessToken,
        });

        if (response.status === 201 && response.body) {
          setMembers((prevMembers) =>
            prevMembers.concat([response.body as IGroupMember])
          );
          showKitSnack(
            t('groups:messageYouJoinedGroup', { group: group?.name }),
            KitSnackDuration.LONG
          );
        } else if (response.status === 409) {
          // ignore - the user is already in the group
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }

          showKitSnack(t('errorUnableToJoinGroup'), KitSnackDuration.SHORT);
        }
      } catch (e) {
        HealthMonitor.logError(e);
      } finally {
        setIsJoiningGroup(false);
      }
    } else {
      if (!isAuthenticated) {
        setShowAuthModal(true);
        setJoinGroupAfterLogin(true);
      }
    }
  }, [accessToken, endUserId, group?.id, group?.name, isAuthenticated, t]);

  const onPressViewInMessaging = useCallback(() => {
    if (Platform.OS === 'web') {
      const channelUrl = `${
        Environment.host
      }/${appKey}/channels/${getShortChannelUrl(channel?.id)}`;

      if (isWithinIframe()) {
        // Open in new tab
        window.open(channelUrl, '_blank');
      } else {
        /**
         * Do not attempt to use window.open using target _self
         * to avoid an infinite page reload issue in small web.
         * See window.opener.location.reload() logic in SmallScreenNavigator.
         * TODO: refactor to use a router to navigate
         */
        // @ts-ignore
        window.location = new URL(channelUrl, window.location).toString();
      }
    } else {
      /**
       * DISP-3013: Open conversation using a native action handler, otherwise
       * the conversation will not open when this button is tapped from a
       * group detail inside the 'groups' handler. The groups handler is used
       * in deep links to directly open a group detail outside the context of
       * Messaging.
       *
       * We must wait for the back/dismiss transition to finish before opening Messaging
       * otherwise the native action may not be dispatched.
       *
       * This is a special case because we're dealing with the transition
       * between two different micro-apps: "chat" and "groups",
       * so we have to use a dismiss + delay workaround here to prevent multiple instances
       * of chat running. Once groups and messaging has been moved to the
       * native-shell, we no longer need this workaround.
       * If we need to do this workaround anywhere else, move this into a helper function.
       */
      const action = {
        handler: 'chat',
        authProviderId: SUBSPLASH_AUTH_PROVIDER_ID,
        moduleCommand: {
          name: 'navigate_group_channel',
          chat_client_channel_id: channel?.id,
          require_login: true,
        },
      };

      if (Platform.OS === 'ios') {
        // iOS will not dispatch the action as expected using the InteractionManager when
        // the Group Detail was opened as a modal, so we must use setTimeout instead.
        // In testing, a 500ms delay was not enough, so we use 1000ms.
        setTimeout(() => {
          dispatchAction(action);
        }, 1000);
      } else {
        // Android will not dispatch the action with a timeout, we must use
        // InteractionManager to ensure the action is dispatched after the back/dismiss transition
        InteractionManager.runAfterInteractions(() => {
          dispatchAction(action);
        });
      }

      // goBack is necessary dismiss group detail modal on large screen native messaging.
      // We use this on all screen sizes for consistency.
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        // If the user navigated to the group detail directly (outside the context of Messaging),
        // we need to dismiss the group detail page using the native bridge.
        dismissReactNativeModal();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey, channel?.id]);

  const fetchGroup = useCallback(() => {
    const _fetchGroup = async (id: string) => {
      setIsFetchingGroup(true);
      try {
        const response = await GroupsService.GetGroup({
          id,
          token: isAuthenticated ? accessToken : undefined,
        });

        if (response.body) {
          setGroup(response.body);
          if (response.body._embedded?.['group-members']) {
            setMembers(response.body._embedded?.['group-members']);
          }
        } else {
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }

          debug('Could not load group details');
          showKitSnack(t('errorUnableToLoadGroup'), KitSnackDuration.SHORT);
          navigation.navigate('Search', { path: 'groups' });
        }
      } catch {}

      setIsFetchingGroup(false);
    };

    if (params.id) _fetchGroup(params.id);

    /**
     * DISP-3448: 'params' is necessary as a dependency in this useEffect
     * so that the group will be fetched to prevent an infinite loading state
     * in the case where:
     * 1. The initial route is group detail (aka direct link to group detail)
     * 2. Then the user navigates back to the groups list
     * 3. Then the user navigates back to the group detail
     *
     * TODO: This is a temporary fix. We should find a better way to handle
     * triggering effects when the route changes. It was surprising that this
     * effect was not triggered with params.id.
     */
  }, [isAuthenticated, accessToken, navigation, t, params]);

  // Hooks
  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  const fetchGroupLocation = useCallback(() => {
    const _fetchGroupLocation = async (id: string) => {
      try {
        const response = await GroupsService.GetGroupLocation({
          id,
          token: accessToken || guestToken,
        });

        if (response.body) {
          setGroupLocation(response.body);
        } else {
          debug('Could not fetch group location');
        }
      } catch {}
    };

    if (group?._embedded?.location?.id) {
      _fetchGroupLocation(group._embedded.location.id);
    } else {
      setGroupLocation(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, navigation, guestToken, group?._embedded?.location?.id]);

  // Hooks
  useEffect(() => {
    fetchGroupLocation();
  }, [fetchGroupLocation]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setTimeout>;

    // If the user is invited to the group, but not in the channel yet, poll the group details to check when the invite has been accepted
    // Their messaging_status will become 'active' when the chat-invite has been accepted (either automatically or by the user)
    if (
      requestToJoinStatus?.status === 'pending' ||
      myselfAsMember?.messaging_status === 'invited'
    ) {
      intervalId = setInterval(() => {
        fetchGroup();
      }, 3000);
    } else if (myselfAsMember?.messaging_status === 'active') {
      setIsJoiningGroup(false);
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [
    fetchGroup,
    myselfAsMember?.messaging_status,
    requestToJoinStatus?.status,
  ]);

  useEffect(() => {
    const fetchJoinRequestStatus = async () => {
      if (isAuthenticated && group?.id) {
        const response = await GroupsService.GetJoinRequest({
          token: accessToken,
          groupId: group.id,
          appKey,
        });

        if (
          response.body?.count !== 0 &&
          response.body?._embedded?.['join-requests'][response.body?.count - 1]
            .status === 'pending'
        ) {
          setRequestToJoinStatus(
            response.body?._embedded?.['join-requests'][
              response.body?.count - 1
            ]
          );
        } else {
          debug('Could not load group details');
          setRequestToJoinStatus(undefined);
        }
      }
    };

    fetchJoinRequestStatus();
  }, [accessToken, appKey, group?.id, isAuthenticated]);

  useEffect(() => {
    const getOrgKey = async () => {
      try {
        const app = await AccountsService.getAppWithBranding(appKey);
        setOrgKey(app?.org_key);
      } catch (e) {
        debug(e);
      }
    };
    getOrgKey();
  }, [appKey]);

  // Fetch channel
  useEffect(() => {
    const fetchChannel = async (groupId: string) => {
      const response = await MessagingChatService.getFiltered(
        'channel',
        [
          ['resource_uuid', groupId],
          ['app_key', appKey],
          ['custom_type', 'group'],
        ],
        appKey,
        accessToken
      );

      if (
        response.data &&
        response.data?.channels &&
        response.data?.channels[0]
      ) {
        setChannel(response.data?.channels[0]);
      } else {
        if (
          response.status &&
          'message' in response &&
          !excludedStatusCodes.includes(response.status)
        ) {
          HealthMonitor.logError(response.message);
        }

        debug('Could not load channel details');
      }
    };

    if (isAuthenticated && myselfAsMember?.id && params.id && appKey) {
      fetchChannel(params.id);
    } else {
      setChannel(undefined);
    }
  }, [appKey, accessToken, isAuthenticated, myselfAsMember?.id, params.id]);

  useEffect(() => {
    return navigation.addListener('blur', () => {
      // Clear state when leaving group details page
      setGroup(undefined);
      setMembers([]);
      setChannel(undefined);
      setOrgKey(undefined);
      setAcceptInviteAfterLogin(false);
      setCreateJoinRequestAfterLogin(false);
      setJoinGroupAfterLogin(false);
    });
  }, [navigation]);

  // Automatically join the group when returning from Auth
  useEffect(() => {
    if (!isFetchingGroup && isAuthenticated && !showAuthModal) {
      if (acceptInviteAfterLogin) {
        if (!myselfAsMember) {
          onPressAcceptInvite();
        }
      } else if (joinGroupAfterLogin) {
        if (!myselfAsMember) {
          onPressJoinGroup();
        }
      } else if (createJoinRequestAfterLogin) {
        if (!myselfAsMember) {
          onCreateRequest();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    myselfAsMember,
    onPressJoinGroup,
    showAuthModal,
    isFetchingGroup,
    createJoinRequestAfterLogin,
    acceptInviteAfterLogin,
    joinGroupAfterLogin,
  ]);

  /**
   * The channel-invites request will throw a 500 error if a chat-user does not
   * exist. A chat-user is created the first time a chat session is created.
   * So we must create a chat session before attempting to accept the invite.
   */
  useEffect(() => {
    const createChatSession = async (appKey: string, token: string) => {
      try {
        await ChatService.createChatSessionV2({
          appKey,
          token,
        });

        setChatUserResolved(true);
      } catch (e) {
        setChatUserResolved(false);

        if (!(e instanceof Error) || !e.message.startsWith('Warning: ')) {
          HealthMonitor.logError(e);
        }
      }
    };

    if (appKey && accessToken && isAuthenticated) {
      createChatSession(appKey, accessToken);
    } else {
      setChatUserResolved(false);
    }
  }, [appKey, accessToken, isAuthenticated]);

  const updateInvitedList = useCallback(() => {
    if (
      channel?.chat_client_channel_id &&
      appKey &&
      accessToken &&
      chatUserResolved
    ) {
      dispatch(
        getInvitedList(channel?.chat_client_channel_id, appKey, accessToken)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel?.chat_client_channel_id, appKey, accessToken, chatUserResolved]);

  useEffect(() => {
    updateInvitedList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  // Display fields
  const memberCount = group?.member_count || 0;
  const groupType = getGroupTypeDisplayName(group?._embedded?.type?.name);
  const subtitle = `${t('groups:membersWithCount', {
    count: memberCount,
  })}${groupType ? ` • ${groupType}` : ''}`;

  const joinButtons = useMemo(() => {
    let joinsButtons: React.ReactElement;

    if (enrollmentType === 'closed') {
      joinsButtons = (
        <KitInfo
          icon='lock'
          iconColor={Colors.N600}
          title={t('enrollmentClosed')}
          subtitle={t('enrollmentClosedDesc')}
          style={{
            flex: sizeClass === SizeClass.Small ? undefined : 1,
            marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
          }}
        />
      );
    } else if (enrollmentType === 'request_to_join') {
      if (requestToJoinStatus?.status !== undefined) {
        if (requestToJoinStatus.status === 'pending') {
          joinsButtons = (
            <KitInfo
              icon='clock'
              iconColor={Colors.N600}
              title={t('infoPendingApproval')}
              subtitle={t('infoRequestedToJoin', {
                date: moment(requestToJoinStatus.created_at).format(
                  'MMM D, yyyy'
                ),
              })}
              style={{
                flex: sizeClass === SizeClass.Small ? undefined : 1,
                marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
              }}
            />
          );
        } else if (requestToJoinStatus.status === 'approved') {
          joinsButtons = (
            <KitButton
              title={t('buttonTitleViewInMessaging')}
              color={Colors.N900}
              small={sizeClass === SizeClass.Medium}
              style={{
                marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
              }}
              onPress={onPressViewInMessaging}
            />
          );
        } else {
          joinsButtons = (
            <KitButton
              title={t('buttonTitleViewInRequestToJoin')}
              color={Colors.N900}
              style={{
                marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
              }}
              onPress={() => setRequestToJoinModalVisible(true)}
            />
          );
        }
      } else {
        joinsButtons = (
          <KitButton
            title={t('buttonTitleViewInRequestToJoin')}
            color={Colors.N900}
            style={{
              marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
            }}
            onPress={() => setRequestToJoinModalVisible(true)}
          />
        );
      }
    } else {
      joinsButtons = (
        <KitButton
          title={t('buttonTitleJoinGroup')}
          color={Colors.N900}
          style={{
            marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
          }}
          onPress={onPressJoinGroup}
          isLoading={isJoiningGroup}
        />
      );
    }

    return joinsButtons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    channel,
    enrollmentType,
    isJoiningGroup,
    requestToJoinStatus?.created_at,
    requestToJoinStatus?.status,
    sizeClass,
    t,
  ]);

  // Loading state
  if (!group) {
    return (
      <View style={{ alignItems: 'center', flex: 1, justifyContent: 'center' }}>
        <KitLoader />
      </View>
    );
  }

  return (
    <>
      <ScrollView
        style={{
          flex: 1,
          backgroundColor: Colors.N0,
          padding: Spacing.l,
        }}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={(_, height) => {
          // This is to support dynamically sizing the Group Finder embed
          postContentHeight(height + 2 * Spacing.l);
          // This is to support dynamically scrolling to top when group details is opened
          postContentScrollToTop();
        }}
      >
        <Show show={Platform.OS === 'web'}>
          <KitButton
            title={t('buttonTitleBackToGroups')}
            secondary
            small
            icon='bar-back-android'
            style={{ marginBottom: Spacing.xxl }}
            onPress={() => navigation.navigate('Search', { path: 'groups' })}
          />
        </Show>
        {/* Wrapping avatar, title, & button/info */}
        <View
          style={{
            flexDirection: sizeClass === SizeClass.Small ? 'column' : 'row',
            borderBottomWidth: sizeClass === SizeClass.Small ? 0 : 1,
            borderBottomColor: Colors.N100,
            paddingBottom: sizeClass === SizeClass.Small ? 0 : Spacing.xl,
            alignItems: sizeClass === SizeClass.Small ? 'stretch' : 'center',
          }}
        >
          {/* Wrapping avatar & title/subtitle */}
          <View
            style={{
              flexDirection: 'row',
              flex: sizeClass === SizeClass.Small ? undefined : 2,
              alignItems: sizeClass === SizeClass.Small ? 'stretch' : 'center',
            }}
          >
            <KitAvatar
              imageUrl={createImageUrl(group._embedded?.image?.id)}
              size={96}
              nickname={group.name}
              style={Depth.m}
            />
            <View
              style={{
                marginLeft: Spacing.l,
                alignSelf: 'center',
                flexShrink: 1,
              }}
            >
              <KitText
                black
                extraBold
                fontSize={sizeClass !== SizeClass.Small ? 36 : 22}
                style={{
                  maxWidth: 400,
                  lineHeight: sizeClass !== SizeClass.Small ? 36 : 22,
                }}
              >
                {group.name}
              </KitText>
              <KitText
                gray
                fontSize={sizeClass !== SizeClass.Small ? 16 : 14}
                style={{ marginTop: Spacing.xs, textTransform: 'capitalize' }}
              >
                {subtitle}
              </KitText>
            </View>
          </View>
          {myselfAsMember &&
          (!myselfAsMember.messaging_status ||
            myselfAsMember.messaging_status === 'active') ? (
            channel ? (
              <KitButton
                title={t('buttonTitleViewInMessaging')}
                color={Colors.N900}
                small={sizeClass === SizeClass.Medium}
                style={{
                  marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
                }}
                onPress={onPressViewInMessaging}
              />
            ) : null
          ) : pendingInviteId ? (
            <KitButton
              title={t('buttonTitleAcceptInvite')}
              color={Colors.N900}
              small={sizeClass === SizeClass.Medium}
              style={{
                marginTop: sizeClass === SizeClass.Small ? Spacing.l : 0,
              }}
              onPress={onPressAcceptInvite}
            />
          ) : !myselfAsMember /* not invited yet */ ||
            inviteExpired /* expired invite */ ? (
            joinButtons
          ) : (
            <></>
          )}
          <KitModalV2
            anchorBottom={sizeClass === SizeClass.Small}
            coverScreen={requestToJoinModalVisible}
            isVisible={requestToJoinModalVisible}
            onClose={() => {
              setRequestToJoinModalVisible(false);
            }}
          >
            <View
              style={{
                width: sizeClass === SizeClass.Small ? '100%' : 620,
              }}
            >
              <Show show={requestToJoinModalVisible}>
                <RequestToJoinModal
                  onCancel={() => setRequestToJoinModalVisible(false)}
                  onConfirm={onCreateRequest}
                  sizeClass={sizeClass}
                />
              </Show>
            </View>
          </KitModalV2>
        </View>
        <View
          style={{
            flexDirection: sizeClass !== SizeClass.Small ? 'row' : 'column',
          }}
        >
          <View
            style={[
              Platform.OS !== 'web' && {
                flex: 2,
              },
              Platform.OS === 'web' && {
                flex: 1,
                marginRight: sizeClass !== SizeClass.Small ? Spacing.xl : 0,
              },
            ]}
          >
            <Show show={Boolean(group.description)}>
              <>
                <Show show={sizeClass !== SizeClass.Small}>
                  <KitText
                    black
                    bold
                    fontSize={16}
                    style={{ marginTop: Spacing.xl, marginBottom: Spacing.xs }}
                  >
                    {t('labelDescription')}
                  </KitText>
                </Show>
                <KitText
                  gray
                  fontSize={16}
                  style={{
                    marginTop: sizeClass !== SizeClass.Small ? 0 : Spacing.xl,
                  }}
                  numberOfLines={4}
                  expandable
                >
                  {group.description}
                </KitText>
              </>
            </Show>
            <Show show={Boolean(managers.length)}>
              <>
                <KitText
                  black
                  bold
                  fontSize={16}
                  style={{ marginTop: Spacing.xl }}
                >
                  {t('labelGroupManagers')}
                </KitText>
                <KitList
                  data={managers}
                  renderItem={({ item, index }) => {
                    try {
                      const profile = item?._embedded['end-user']?._embedded
                        ?.profiles[0] as IProfile;
                      const photoId =
                        item?._embedded?.['end-user']?._embedded?.profiles?.[0]
                          ?._embedded?.photo?.id ?? '';
                      const imagePhotoUrl = createImageUrl(photoId);

                      return (
                        <KitListItem
                          ImageComponent={
                            <KitAvatar
                              nickname={`${profile.first_name} ${profile.last_name}`}
                              size={Spacing.xxl}
                              imageUrl={imagePhotoUrl}
                            />
                          }
                          title={`${profile.first_name} ${profile.last_name}`}
                          topBorder={
                            sizeClass === SizeClass.Small
                              ? index === 0
                              : undefined
                          }
                          bottomBorder={sizeClass === SizeClass.Small}
                        />
                      );
                    } catch (e) {
                      return null;
                    }
                  }}
                  style={{ marginTop: Spacing.s }}
                />
              </>
            </Show>
          </View>
          <View
            style={[
              Platform.OS !== 'web' && { flex: 1 },
              Platform.OS === 'web' && {
                width: sizeClass === SizeClass.Small ? '100%' : 250,
              },
            ]}
          >
            {groupSchedule !== '' ? (
              <View>
                <KitText
                  color={Colors.N900}
                  bold
                  fontSize={16}
                  style={{ marginTop: Spacing.xl, marginBottom: Spacing.xs }}
                >
                  {t('schedule:headerTitle')}
                </KitText>
                <KitText
                  fontSize={16}
                  lineHeight={22}
                  color={
                    sizeClass === SizeClass.Small ? Colors.N500 : Colors.N900
                  }
                  style={{
                    flex: 1,
                    marginBottom: Spacing.xl,
                  }}
                  ellipsizeMode='tail'
                  numberOfLines={1}
                >
                  {groupSchedule}
                </KitText>
              </View>
            ) : null}
            {groupLocation ? (
              <View
                style={{
                  flex: 1,
                  marginTop: groupSchedule !== '' ? 0 : Spacing.xl,
                  marginBottom: Platform.OS !== 'web' ? Spacing.xxxl : 0,
                }}
              >
                <KitText
                  color={Colors.N900}
                  bold
                  fontSize={16}
                  style={{ marginBottom: Spacing.xs }}
                >
                  {t('labelGroupLocation')}
                </KitText>
                <KitText
                  fontSize={16}
                  lineHeight={22}
                  ellipsizeMode='tail'
                  numberOfLines={3}
                  color={
                    sizeClass === SizeClass.Small ? Colors.N500 : Colors.N900
                  }
                >
                  {displayAddress}
                </KitText>
                <View
                  style={{
                    flex: 1,
                    borderRadius: 8,
                    width: '100%',
                    marginTop: Spacing.s,
                  }}
                >
                  {groupLocation?._embedded.address.latitude &&
                  groupLocation?._embedded.address.longitude ? (
                    <View
                      style={{
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      <KitMapView
                        latitude={groupLocation?._embedded.address.latitude}
                        longitude={groupLocation?._embedded.address.longitude}
                        circle={
                          myselfAsMember
                            ? undefined
                            : {
                                radius: 3218.68,
                                strokeColor: Colors.N1000,
                                fillColor: 'rgba(0, 0, 0, 0.2)',
                                strokeOpacity: 0.8,
                                fillOpacity: 0.8,
                                strokeWeight: 0.6,
                              }
                        }
                        zoom={myselfAsMember ? 14 : 11}
                        webStyle={{ paddingBottom: '50%', borderRadius: 8 }}
                        nativeStyle={{
                          paddingBottom: '50%',
                        }}
                        hideZoomOptions={true}
                        marker={
                          myselfAsMember
                            ? { title: groupLocation?.title }
                            : undefined
                        }
                        pointerEvents='none'
                        scrollEnabled={false}
                        zoomEnabled={false}
                      />
                    </View>
                  ) : null}
                </View>
              </View>
            ) : null}
          </View>
        </View>
      </ScrollView>
      <KitSnackRender
        {...snackOptions}
        setVisible={(value) =>
          setSnackOptions({ ...snackOptions, visible: value })
        }
      />
      <AuthModal
        appKey={appKey}
        orgKey={orgKey}
        returnUrl={`${Environment.host}/${appKey}/auth`}
        onClose={() => {
          setShowAuthModal(false);
        }}
        isVisible={showAuthModal}
        targetAuthProviderId={SUBSPLASH_AUTH_PROVIDER_ID}
      />
    </>
  );
};
